import { useState, useEffect, useCallback, useRef } from 'react';
import './AdminPage.css';
import { ToastProvider, useToast } from '../components/admin/Toast';
import AdminSidebar from './admin/AdminSidebar';
import AdminDashboard from './admin/AdminDashboard';
import AdminSubscriptions from './admin/AdminSubscriptions';
import AdminCustomers from './admin/AdminCustomers';
import AdminEmailLogs from './admin/AdminEmailLogs';
import AdminServers from './admin/AdminServers';
import AdminDeployments from './admin/AdminDeployments';
import AdminPlans from './admin/AdminPlans';
import AdminWaitlist from './admin/AdminWaitlist';
import AdminOffers from './admin/AdminOffers';
import AdminAffiliates from './admin/AdminAffiliates';
import AdminSupport from './admin/AdminSupport';
import AdminMigration from './admin/AdminMigration';
import AdminUsers from './admin/AdminUsers';
import AdminAuditLog from './admin/AdminAuditLog';
import AdminRevenue from './admin/AdminRevenue';
import AdminAnalytics from './admin/AdminAnalytics';
import AdminMonitoring from './admin/AdminMonitoring';
import AdminCancellations from './admin/AdminCancellations';
import AdminAnnouncements from './admin/AdminAnnouncements';
import AdminEmailQueue from './admin/AdminEmailQueue';
import AdminSettings from './admin/AdminSettings';
import AdminApiKeys from './admin/AdminApiKeys';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const sectionLabels = {
  dashboard: 'Dashboard',
  subscriptions: 'Subscriptions',
  customers: 'Customers',
  emaillogs: 'Email Logs',
  servers: 'Servers',
  deployments: 'Deployments',
  plans: 'Plans',
  offers: 'Offers & Coupons',
  affiliates: 'Affiliates',
  support: 'Support',
  migration: 'Price Migration',
  users: 'Admin Users',
  auditlog: 'Audit Log',
  analytics: 'Marketing Attribution',
  revenue: 'Revenue',
  monitoring: 'Monitoring',
  cancellations: 'Cancellation Insights',
  waitlist: 'Waitlist & Pre-launch',
  apikeys: 'API Keys',
  settings: 'Settings',
};

function AdminPageInner() {
  const [authMode, setAuthMode] = useState('email'); // 'token' or 'email'
  const [token, setToken] = useState(() => localStorage.getItem('openhosst_admin_token') || '');
  const [jwt, setJwt] = useState(() => localStorage.getItem('openhosst_admin_jwt') || '');
  const [adminUser, setAdminUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('openhosst_admin_user')); } catch { return null; }
  });
  const [tokenInput, setTokenInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('section') || 'dashboard';
  });
  const [supportOpenTicketId, setSupportOpenTicketId] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('ticket') ? parseInt(p.get('ticket'), 10) : null;
  });
  const adminPushActiveRef = useRef(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [selectedSubs, setSelectedSubs] = useState(new Set());
  const toast = useToast();
  const [alertCount, setAlertCount] = useState(0);
  const [supportUnread, setSupportUnread] = useState(0);
  const [pushBannerDismissed, setPushBannerDismissed] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isStandalone] = useState(() => typeof window !== 'undefined' && !!(window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true));

  // In-app notifications
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [adminUnreadCount, setAdminUnreadCount] = useState(0);
  const [adminNotifOpen, setAdminNotifOpen] = useState(false);
  const adminNotifRef = useRef(null);

  const apiFetch = useCallback((path, options = {}) => {
    const headers = { ...options.headers };
    if (jwt) {
      headers['Authorization'] = `Bearer ${jwt}`;
    }
    if (token && !jwt) {
      headers['X-Admin-Token'] = token;
    }
    return fetch(`${API_URL}${path}`, { ...options, headers });
  }, [token, jwt]);

  // Support unread badge — count of tickets with unread customer messages
  const refreshSupportUnread = useCallback(() => {
    apiFetch('/api/admin/support/unread-count')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setSupportUnread(d.tickets || 0); })
      .catch(() => {});
  }, [apiFetch]);

  useEffect(() => {
    if (!authed) return;
    refreshSupportUnread();
    const t = setInterval(refreshSupportUnread, 30000);
    return () => clearInterval(t);
  }, [authed, refreshSupportUnread]);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/admin/subscriptions');
      if (res.status === 401) {
        setError('Session expired');
        handleLogout();
        return;
      }
      const data = await res.json();
      setSubscriptions(data.subscriptions || []);
      setAuthed(true);
    } catch (err) {
      setError('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (token || jwt) fetchSubscriptions();
  }, [token, jwt, fetchSubscriptions]);

  // Poll active alerts for badge count
  useEffect(() => {
    if (!authed || (adminUser?.role && adminUser.role !== 'super_admin')) return;
    const loadAlerts = async () => {
      try {
        const res = await apiFetch('/api/admin/monitoring/alerts/active');
        if (res.ok) { const data = await res.json(); setAlertCount(data.length); }
      } catch {}
    };
    loadAlerts();
    const interval = setInterval(loadAlerts, 60000);
    return () => clearInterval(interval);
  }, [authed, adminUser, apiFetch]);

  // Load admin notifications on mount
  useEffect(() => {
    if (!authed) return;
    apiFetch('/api/admin/notifications')
      .then(r => r.json())
      .then(data => {
        setAdminNotifications(data.notifications || []);
        setAdminUnreadCount(data.unreadCount || 0);
      })
      .catch(() => {});
  }, [authed, apiFetch]);

  // Admin push: 'granted' | 'prompt' (show Enable button) | 'denied' | 'ios-install' | 'unsupported'
  const [adminPushState, setAdminPushState] = useState('unknown');

  const subscribeAdminPush = useCallback(async () => {
    await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
    const reg = await navigator.serviceWorker.ready;
    if (!reg || !reg.pushManager) throw new Error('Push not available in this browser');
    const keyRes = await apiFetch('/api/push/vapid-public-key');
    if (!keyRes.ok) throw new Error('VAPID key fetch failed (' + keyRes.status + ')');
    const { publicKey } = await keyRes.json();
    if (!publicKey) throw new Error('No VAPID public key configured on the server');
    const u2a = (b64) => { const pad = '='.repeat((4 - b64.length % 4) % 4); const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/')); return Uint8Array.from([...raw].map(c => c.charCodeAt(0))); };
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: u2a(publicKey) });
    const saveRes = await apiFetch('/api/admin/push/subscribe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...sub.toJSON(), origin: window.location.origin }) });
    if (!saveRes.ok) throw new Error('Server did not accept the subscription (' + saveRes.status + ')');
    adminPushActiveRef.current = true;
    return true;
  }, [apiFetch]);

  // Triggered from a click — Safari (macOS + iOS) require Notification.requestPermission()
  // to run inside a user gesture, not on page load (that was the bug). Outcomes surfaced.
  const enableAdminPush = useCallback(async () => {
    try {
      if (!('Notification' in window)) { alert('This browser does not support notifications.'); return; }
      let perm = Notification.permission;
      if (perm === 'default') perm = await Notification.requestPermission();
      if (perm === 'denied') { setAdminPushState('denied'); alert('Notifications are blocked for this site. Allow them in your browser’s site settings, then reload.'); return; }
      if (perm !== 'granted') { setAdminPushState('prompt'); return; }
      await subscribeAdminPush();
      setAdminPushState('granted');
      alert('Notifications enabled ✓');
    } catch (e) {
      setAdminPushState('prompt');
      alert('Could not enable notifications: ' + (e && e.message ? e.message : String(e)));
    }
  }, [subscribeAdminPush]);

  const sendTestPush = useCallback(async () => {
    try {
      const r = await apiFetch('/api/admin/push/test', { method: 'POST' });
      const d = await r.json();
      if (!r.ok) { alert('Test failed: ' + (d.error || r.status)); return; }
      alert(`Test push sent to ${d.sent}/${d.subscriptions} subscription(s).\n\nIf nothing appears in THIS browser, check macOS System Settings → Notifications → your browser (allow notifications + banners), and turn off Focus / Do Not Disturb.`);
    } catch (e) { alert('Test failed: ' + (e && e.message ? e.message : e)); }
  }, [apiFetch]);

  // On load: never prompt; register SW early and silently (re)subscribe only if already granted.
  useEffect(() => {
    if (!authed) return;
    const ua = navigator.userAgent || '';
    const isIOS = /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setAdminPushState(isIOS && !isStandalone ? 'ios-install' : 'unsupported');
      return;
    }
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => {});
    if (Notification.permission === 'granted') {
      subscribeAdminPush().then(() => setAdminPushState('granted')).catch(() => setAdminPushState('prompt'));
    } else if (Notification.permission === 'denied') {
      setAdminPushState('denied');
    } else {
      setAdminPushState('prompt');
    }
  }, [authed, subscribeAdminPush]);

  // Android/Chromium: capture the install prompt so we can offer one-tap install
  // (an installed PWA is the most reliable place for mobile push).
  useEffect(() => {
    const onBIP = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = () => setInstallPrompt(null);
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    return () => { window.removeEventListener('beforeinstallprompt', onBIP); window.removeEventListener('appinstalled', onInstalled); };
  }, []);

  const installApp = useCallback(() => {
    if (!installPrompt) return;
    installPrompt.prompt();
    if (installPrompt.userChoice) installPrompt.userChoice.finally(() => setInstallPrompt(null));
  }, [installPrompt]);

  // SSE connection for live admin notifications
  useEffect(() => {
    if (!authed) return;
    const streamToken = jwt || token;
    if (!streamToken) return;
    const es = new EventSource(`${API_URL}/api/admin/notifications/stream?token=${encodeURIComponent(streamToken)}`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.event === 'init') { setAdminUnreadCount(data.unread || 0); return; }
        // Ephemeral events — pass through to ticket views only, no notification
        if (data.event === 'typing' || data.event === 'read' || data.event === 'support_status') {
          window.dispatchEvent(new CustomEvent('openhosst:notification', { detail: data }));
          return;
        }
        setAdminNotifications(prev => [data, ...prev].slice(0, 50));
        setAdminUnreadCount(n => n + 1);
        // Only show toast + sound if OS push is not active (incognito / no permission)
        if (!adminPushActiveRef.current) {
          const ex = data.extra || {};
          toast.info(data.title, data.message, { ...ex, notifType: data.type });
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.frequency.setValueAtTime(880, ctx.currentTime);
            o.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
            g.gain.setValueAtTime(0.15, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
            o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.35);
          } catch {}
        }
        // Broadcast so open ticket chat can refresh immediately
        window.dispatchEvent(new CustomEvent('openhosst:notification', { detail: data }));
      } catch {}
    };
    es.onerror = () => {};
    // Dispatch reload event on RECONNECT only (not the initial connection)
    let _sseOpened = false;
    es.onopen = () => {
      if (_sseOpened) window.dispatchEvent(new CustomEvent('openhosst:sse-reconnect'));
      _sseOpened = true;
    };
    return () => es.close();
  }, [authed, jwt, token]);

  // Close admin notif dropdown on outside click
  useEffect(() => {
    if (!adminNotifOpen) return;
    const handler = (e) => {
      if (adminNotifRef.current && !adminNotifRef.current.contains(e.target)) setAdminNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [adminNotifOpen]);

  const handleAdminNotifClick = async (notif) => {
    if (!notif.read) {
      apiFetch(`/api/admin/notifications/${notif.id}/read`, { method: 'POST' }).catch(() => {});
      setAdminNotifications(prev => prev.filter(n => n.id !== notif.id));
      setAdminUnreadCount(n => Math.max(0, n - 1));
    }
    setAdminNotifOpen(false);
    if (notif.link) setSupportOpenTicketId(notif.link);
    setActiveSection('support');
  };

  const handleAdminMarkAllRead = async () => {
    apiFetch('/api/admin/notifications/read-all', { method: 'POST' }).catch(() => {});
    setAdminNotifications([]);
    setAdminUnreadCount(0);
  };

  // Lockout countdown timer
  useEffect(() => {
    if (lockoutSeconds <= 0) return;
    const t = setTimeout(() => setLockoutSeconds(s => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [lockoutSeconds]);

  const handleTokenSubmit = async (e) => {
    e.preventDefault();
    const t = tokenInput.trim();
    if (!t) return;
    setLoginLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions`, {
        headers: { 'X-Admin-Token': t }
      });
      if (res.status === 401) {
        setError('Invalid admin token');
        setLoginLoading(false);
        return;
      }
      localStorage.setItem('openhosst_admin_token', t);
      setToken(t);
      setAdminUser({ id: 0, email: 'legacy-token', full_name: 'Admin', role: 'super_admin' });
      localStorage.setItem('openhosst_admin_user', JSON.stringify({ id: 0, email: 'legacy-token', full_name: 'Admin', role: 'super_admin' }));
      setAuthed(true);
    } catch {
      setError('Connection failed');
    }
    setLoginLoading(false);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailInput.trim() || !passwordInput.trim() || lockoutSeconds > 0) return;
    setLoginLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailInput.trim(), password: passwordInput }),
      });
      const data = await res.json();
      if (res.status === 429) {
        setLockoutSeconds(data.retryAfter || 900);
        setError(data.error || 'Too many attempts. Please wait.');
        setLoginLoading(false);
        return;
      }
      if (!res.ok) {
        setError(data.error || 'Login failed');
        setLoginLoading(false);
        return;
      }
      localStorage.setItem('openhosst_admin_jwt', data.token);
      localStorage.setItem('openhosst_admin_user', JSON.stringify(data.user));
      setJwt(data.token);
      setAdminUser(data.user);
      setAuthed(true);
    } catch {
      setError('Connection failed');
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('openhosst_admin_token');
    localStorage.removeItem('openhosst_admin_jwt');
    localStorage.removeItem('openhosst_admin_user');
    setToken('');
    setJwt('');
    setAdminUser(null);
    setTokenInput('');
    setEmailInput('');
    setPasswordInput('');
    setAuthed(false);
    setSubscriptions([]);
  };

  const refreshSubscriptions = () => {
    fetchSubscriptions();
    toast.success('Refreshed', 'Subscription data updated');
  };

  // Login screen
  if ((!token && !jwt) || !authed) {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <div className="admin-login-logo">
            <img src="/logo-dark.png" alt="OpenHosst" className="admin-login-logo-img" />
          </div>
          <h1 className="admin-login-title">Admin Console</h1>
          <p className="admin-login-subtitle">Restricted access — authorised personnel only</p>
          {error && (
            <div className="admin-login-error">
              <i className="fa-solid fa-circle-exclamation" style={{ marginRight: '6px' }} />
              {error}
              {lockoutSeconds > 0 && <span className="admin-login-lockout-timer"> ({Math.floor(lockoutSeconds / 60)}:{String(lockoutSeconds % 60).padStart(2, '0')} remaining)</span>}
            </div>
          )}

          <div className="admin-login-tabs">
            <button className={`admin-login-tab${authMode === 'email' ? ' active' : ''}`} onClick={() => { setAuthMode('email'); setError(''); }}>Email</button>
            <button className={`admin-login-tab${authMode === 'token' ? ' active' : ''}`} onClick={() => { setAuthMode('token'); setError(''); }}>Token</button>
          </div>

          {authMode === 'email' ? (
            <form onSubmit={handleEmailSubmit}>
              <div className="admin-login-input-wrapper" style={{ marginBottom: '10px' }}>
                <input
                  type="email"
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  placeholder="Email address"
                  className="admin-input"
                  autoFocus
                />
              </div>
              <div className="admin-login-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwordInput}
                  onChange={e => setPasswordInput(e.target.value)}
                  placeholder="Password"
                  className="admin-input"
                />
                <button
                  type="button"
                  className="admin-login-eye-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                >
                  <i className={showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'} />
                </button>
              </div>
              <button
                type="submit"
                className="admin-btn admin-btn--primary admin-login-btn"
                disabled={loginLoading || !emailInput.trim() || !passwordInput.trim() || lockoutSeconds > 0}
              >
                {loginLoading ? <span className="admin-spinner" /> : lockoutSeconds > 0 ? `Locked (${lockoutSeconds}s)` : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleTokenSubmit}>
              <div className="admin-login-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={tokenInput}
                  onChange={e => setTokenInput(e.target.value)}
                  placeholder="Enter admin token"
                  className="admin-input"
                  autoFocus
                />
                <button
                  type="button"
                  className="admin-login-eye-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                >
                  <i className={showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'} />
                </button>
              </div>
              <button
                type="submit"
                className="admin-btn admin-btn--primary admin-login-btn"
                disabled={loginLoading || !tokenInput.trim()}
              >
                {loginLoading ? <span className="admin-spinner" /> : 'Sign In'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  const renderSection = () => {
    const sectionProps = { apiFetch, token, toast, refreshSupportUnread };
    switch (activeSection) {
      case 'dashboard':
        return <AdminDashboard subscriptions={subscriptions} onNavigate={setActiveSection} toast={toast} />;
      case 'customers':
        return <AdminCustomers {...sectionProps} />;
      case 'emaillogs':
        return <AdminEmailLogs {...sectionProps} />;
      case 'subscriptions':
        return <AdminSubscriptions
          {...sectionProps} subscriptions={subscriptions}
          refreshSubscriptions={refreshSubscriptions}
          selectedSubs={selectedSubs} setSelectedSubs={setSelectedSubs}
          showOffers={false}
        />;
      case 'servers':
        return <AdminServers {...sectionProps} />;
      case 'deployments':
        return <AdminDeployments
          subscriptions={subscriptions} {...sectionProps}
          refreshSubscriptions={refreshSubscriptions}
        />;
      case 'plans':
        return <AdminPlans {...sectionProps} />;
      case 'offers':
        return <AdminOffers
          {...sectionProps} subscriptions={subscriptions}
          selectedSubs={selectedSubs} setSelectedSubs={setSelectedSubs}
        />;
      case 'affiliates':
        return <AdminAffiliates {...sectionProps} />;
      case 'support':
        return <AdminSupport {...sectionProps} initialTicketId={supportOpenTicketId} />;
      case 'migration':
        return <AdminMigration {...sectionProps} />;
      case 'users':
        return <AdminUsers {...sectionProps} />;
      case 'auditlog':
        return <AdminAuditLog {...sectionProps} />;
      case 'analytics':
        return <AdminAnalytics {...sectionProps} />;
      case 'revenue':
        return <AdminRevenue {...sectionProps} />;
      case 'monitoring':
        return <AdminMonitoring {...sectionProps} />;
      case 'waitlist':
        return <AdminWaitlist {...sectionProps} />;
      case 'cancellations':
        return <AdminCancellations {...sectionProps} />;
      case 'announcements':
        return <AdminAnnouncements {...sectionProps} />;
      case 'emailqueue':
        return <AdminEmailQueue {...sectionProps} />;
      case 'apikeys':
        return <AdminApiKeys {...sectionProps} />;
      case 'settings':
        return <AdminSettings {...sectionProps} />;
      default:
        return <AdminDashboard subscriptions={subscriptions} onNavigate={setActiveSection} toast={toast} />;
    }
  };

  const displayName = adminUser?.full_name || 'Admin';
  const displayRole = adminUser?.role ? adminUser.role.replace('_', ' ') : 'Admin';
  const initials = displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'A';

  return (
    <div className="admin-layout">
      {mobileSidebarOpen && <div className="admin-sidebar-backdrop open" onClick={() => setMobileSidebarOpen(false)} />}
      <AdminSidebar
        activeSection={activeSection}
        onNavigate={(s) => { setActiveSection(s); setMobileSidebarOpen(false); }}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(v => !v)}
        mobileOpen={mobileSidebarOpen}
        adminUser={adminUser}
        badges={{ support: supportUnread }}
      />
      <main className={`admin-content ${sidebarCollapsed ? 'admin-content--expanded' : ''}`}>
        <div className="admin-topbar">
          <div className="admin-topbar-left">
            <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => { if (window.innerWidth <= 768) setMobileSidebarOpen(v => !v); else setSidebarCollapsed(v => !v); }}>
              <i className="fa-solid fa-bars" />
            </button>
            <span className="admin-topbar-breadcrumb">
              <i className="fa-solid fa-chevron-right" style={{ fontSize: '9px', marginRight: '8px', opacity: 0.4 }} />
              {sectionLabels[activeSection] || 'Dashboard'}
            </span>
          </div>
          <div className="admin-topbar-right">
            {loading && <span style={{ color: 'var(--admin-text-muted)', fontSize: '12px' }}><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '4px' }} />Loading...</span>}
            <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={fetchSubscriptions} title="Refresh data">
              <i className="fa-solid fa-arrows-rotate" />
            </button>
            {adminPushState === 'prompt' && (
              <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={enableAdminPush} title="Enable OS notifications" style={{ color: '#f59e0b' }}>
                <i className="fa-solid fa-bell-slash" />
              </button>
            )}
            {adminPushState === 'ios-install' && (
              <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => alert('To get notifications on iPhone/iPad: open the admin panel in Safari, tap the Share button, choose “Add to Home Screen”, then open it from that icon and tap the bell to enable notifications.')} title="Add to Home Screen for notifications" style={{ color: '#f59e0b' }}>
                <i className="fa-solid fa-bell-slash" />
              </button>
            )}
            {adminPushState === 'granted' && (
              <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={sendTestPush} title="Send a test notification to this browser">
                <i className="fa-solid fa-paper-plane" />
              </button>
            )}
            <div className="admin-notif-wrapper" ref={adminNotifRef}>
              <button
                className="admin-btn admin-btn--ghost admin-btn--sm"
                title="Notifications"
                style={{ position: 'relative' }}
                onClick={() => setAdminNotifOpen(v => !v)}
              >
                <i className="fa-solid fa-bell" />
                {adminUnreadCount > 0 && (
                  <span className="admin-notif-badge">{adminUnreadCount > 9 ? '9+' : adminUnreadCount}</span>
                )}
              </button>

              {adminNotifOpen && (
                <div className="admin-notif-dropdown">
                  <div className="admin-notif-header">
                    <span>Notifications</span>
                    {adminUnreadCount > 0 && (
                      <button onClick={handleAdminMarkAllRead}>Mark all read</button>
                    )}
                  </div>
                  <div className="admin-notif-list">
                    {adminNotifications.filter(n => !n.read).length === 0 ? (
                      <div className="admin-notif-empty">
                        <i className="fa-solid fa-bell-slash" style={{ marginRight: 8 }} />
                        No new notifications
                      </div>
                    ) : adminNotifications.filter(n => !n.read).map(n => {
                      const diff = Date.now() - new Date(n.created_at).getTime();
                      const s = Math.floor(diff / 1000);
                      const timeStr = s < 60 ? 'just now' : s < 3600 ? `${Math.floor(s/60)}m ago` : s < 86400 ? `${Math.floor(s/3600)}h ago` : `${Math.floor(s/86400)}d ago`;
                      return (
                        <div
                          key={n.id}
                          className="admin-notif-item unread" 
                          onClick={() => handleAdminNotifClick(n)}
                        >
                          <div className="admin-notif-title">{n.title}</div>
                          <div className="admin-notif-msg">{n.message}</div>
                          <div className="admin-notif-time">{timeStr}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <span style={{ color: 'var(--admin-text-muted)', fontSize: '12px', padding: '0 4px' }}>{displayName}</span>
            <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={handleLogout} title="Sign out">
              <i className="fa-solid fa-right-from-bracket" />
            </button>
          </div>
        </div>
        <div className="admin-main">
          {!pushBannerDismissed && (adminPushState === 'prompt' || adminPushState === 'ios-install' || (installPrompt && !isStandalone)) && (
            <div className="admin-alert" style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', background: 'rgba(64,224,208,0.08)', border: '1px solid rgba(64,224,208,0.3)' }}>
              <i className="fa-solid fa-bell" style={{ color: '#40E0D0', fontSize: '16px', flexShrink: 0 }} />
              {adminPushState === 'ios-install' ? (
                <span style={{ flex: 1, minWidth: '220px', fontSize: '13px' }}>
                  <strong>On iPhone / iPad:</strong> tap the <i className="fa-solid fa-arrow-up-from-bracket" /> Share button in Safari → <strong>Add to Home Screen</strong>, then open OpenHosst from that new icon and tap <strong>Enable notifications</strong>.
                </span>
              ) : (
                <>
                  <span style={{ flex: 1, minWidth: '200px', fontSize: '13px' }}>
                    Turn on notifications to get alerted about new support tickets.
                    {installPrompt && !isStandalone && <span style={{ color: 'var(--admin-text-muted)' }}> Installing the app is the most reliable option on mobile.</span>}
                  </span>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0, flexWrap: 'wrap' }}>
                    {adminPushState === 'prompt' && (
                      <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={enableAdminPush}>Enable notifications</button>
                    )}
                    {installPrompt && !isStandalone && (
                      <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={installApp}><i className="fa-solid fa-download" style={{ marginRight: '4px' }} />Install app</button>
                    )}
                  </div>
                </>
              )}
              <button onClick={() => setPushBannerDismissed(true)} title="Dismiss" style={{ background: 'none', border: 'none', color: 'var(--admin-text-muted)', cursor: 'pointer', flexShrink: 0 }}>
                <i className="fa-solid fa-times" />
              </button>
            </div>
          )}
          {error && <div className="admin-alert admin-alert--error" style={{ marginBottom: '16px' }}>{error}</div>}
          <div className="admin-section-animate" key={activeSection}>
            {renderSection()}
          </div>
        </div>
      </main>
    </div>
  );
}

function AdminPage() {
  return (
    <ToastProvider>
      <AdminPageInner />
    </ToastProvider>
  );
}

export default AdminPage;
